/**
 * Sprint 232 — Influencer Discovery V2 (Creator Marketplace)
 *
 * Premium, visual creator-marketplace experience. Large clickable cards,
 * tier + category filters, intro video preview, swipeable portfolio
 * thumbnails, "Worked With" brand credibility chips, side-drawer preview
 * with full-profile deep link.
 *
 * NOTE: This page is SCOPED TO INFLUENCERS ONLY. The standard Talent
 * Discovery page (TalentDiscovery.js) remains untouched per product
 * direction.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, Search, Filter, Sparkles, MapPin, Heart, Loader2,
  CheckCircle2, RefreshCw, X, Users, Award, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import CreatorPreviewDrawer from '../../components/CreatorPreviewDrawer';
import InfluencerCard from '../../components/InfluencerCard';
import InfluencerDiscovery from './InfluencerDiscovery'; // legacy fallback for ?classic=1

const BRAND = '#0DEAE3';

const InfluencerDiscoveryV2 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isClassic = searchParams.get('classic') === '1';

  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({
    tiers: [],
    categories: [],
    content_types: [],
    platforms: [],
    creator_types: [],
    creator_statuses: [],
  });
  const [favIds, setFavIds] = useState(new Set());
  const [previewId, setPreviewId] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    tier: '',
    category: '',
    verifiedOnly: false,
    city: '',
    search: '',
    // Iter-252 — Creator Network filters: 'all' = no filter
    creatorType: 'all',           // 'all' | 'influencer' | 'content_creator' | 'both'
    platform: '',                 // 'instagram' | 'tiktok' | 'youtube' | 'facebook' | 'twitter' | 'linkedin'
    contentType: '',              // 'Reels' | 'UGC' | …
    audienceLocation: '',         // free-text
    creatorStatus: '',            // 'verified' | 'pending' | 'not_submitted'
    minFollowers: '',
    maxFollowers: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchFavIds = useCallback(async () => {
    try {
      const res = await api.get('/client/favorites');
      setFavIds(new Set((res.data || []).map(f => f.talent_id)));
    } catch {/* swallow */}
  }, []);

  const fetchMeta = useCallback(async () => {
    try {
      const res = await api.get('/influencer/meta');
      setMeta(res.data || { tiers: [], categories: [] });
    } catch {/* default fallback already set */}
  }, []);

  const [reloadCounter, setReloadCounter] = useState(0);

  // Sprint 233 — Package selection mode (came from /client/packages/<slug>)
  const packageSlug = searchParams.get('package') || '';
  const [pickedForPackage, setPickedForPackage] = useState([]);
  useEffect(() => {
    if (!packageSlug) { setPickedForPackage([]); return; }
    try {
      const raw = sessionStorage.getItem(`pkg-select-${packageSlug}`);
      setPickedForPackage(raw ? JSON.parse(raw) : []);
    } catch { setPickedForPackage([]); }
  }, [packageSlug]);

  // Hydrate tier/category from URL the first time the page mounts in package-mode
  useEffect(() => {
    const t = searchParams.get('tier');
    const c = searchParams.get('category');
    if (t || c) {
      setFilters(prev => ({ ...prev, tier: t || prev.tier, category: c || prev.category }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePackagePick = (influencer) => {
    if (!packageSlug) return;
    const id = influencer.user_id;
    const exists = pickedForPackage.find(x => (x.user_id || x.id) === id);
    let next;
    if (exists) {
      next = pickedForPackage.filter(x => (x.user_id || x.id) !== id);
    } else {
      next = [...pickedForPackage, {
        user_id: id, name: influencer.name, photo: influencer.photo,
        influencer_tier: influencer.influencer_tier,
      }];
    }
    setPickedForPackage(next);
    sessionStorage.setItem(`pkg-select-${packageSlug}`, JSON.stringify(next));
  };

  // Sprint 233.B — Card actions: Invite + Compare
  const [compareIds, setCompareIds] = useState(new Set());
  const handleInvite = (inf) => {
    // Default invite: jump to discover detail with the action prefilled.
    navigate(`/client/discover/${inf.user_id}?action=book`);
  };
  const toggleCompare = (inf) => {
    setCompareIds(prev => {
      const next = new Set(prev);
      if (next.has(inf.user_id)) {
        next.delete(inf.user_id);
      } else {
        if (next.size >= 4) {
          // soft cap: compare up to 4
          return prev;
        }
        next.add(inf.user_id);
      }
      return next;
    });
  };

  const finishPackageSelection = () => {
    sessionStorage.setItem(`pkg-select-${packageSlug}`, JSON.stringify(pickedForPackage));
    navigate(`/client/packages/${packageSlug}`);
  };

  useEffect(() => { if (!isClassic) { fetchMeta(); fetchFavIds(); } }, [fetchMeta, fetchFavIds, isClassic]);
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (isClassic) return;
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.tier) params.append('tier', filters.tier);
        if (filters.category) params.append('category', filters.category);
        if (filters.verifiedOnly) params.append('verified_only', 'true');
        if (filters.city) params.append('city', filters.city);
        if (filters.search) params.append('search', filters.search);
        // Iter-247 — Creator Network filters
        if (filters.creatorType && filters.creatorType !== 'both') params.append('creator_type', filters.creatorType);
        if (filters.platform) params.append('platform', filters.platform);
        if (filters.contentType) params.append('content_type', filters.contentType);
        if (filters.audienceLocation) params.append('audience_location', filters.audienceLocation);
        if (filters.creatorStatus) params.append('creator_status', filters.creatorStatus);
        if (filters.minFollowers) params.append('min_followers', filters.minFollowers);
        if (filters.maxFollowers) params.append('max_followers', filters.maxFollowers);
        params.append('limit', '60');
        const res = await api.get(`/influencer/discover?${params.toString()}`);
        if (!cancelled) setInfluencers(res.data || []);
      } catch {
        if (!cancelled) toast.error('Failed to load influencers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [filters, isClassic, reloadCounter]);

  const activeFilterCount = useMemo(() => [
    filters.tier, filters.category, filters.verifiedOnly, filters.city,
    filters.creatorType !== 'all' ? filters.creatorType : '',
    filters.platform, filters.contentType, filters.audienceLocation,
    filters.creatorStatus, filters.minFollowers, filters.maxFollowers,
  ].filter(Boolean).length, [filters]);

  const previewInfluencer = useMemo(
    () => influencers.find(i => i.user_id === previewId) || null,
    [influencers, previewId],
  );

  // ---- Classic escape hatch (preserves legacy view) — rendered AFTER hooks ----
  if (isClassic) {
    return <InfluencerDiscovery />;
  }

  const toggleFav = async (talentId) => {
    try {
      if (favIds.has(talentId)) {
        await api.delete(`/client/favorites/${talentId}`);
        setFavIds(prev => { const n = new Set(prev); n.delete(talentId); return n; });
        toast.success('Removed from favorites');
      } else {
        await api.post('/client/favorites', { talent_id: talentId });
        setFavIds(prev => new Set([...prev, talentId]));
        toast.success('Added to favorites');
      }
    } catch { toast.error('Failed to update favorite'); }
  };

  const clearFilters = () => setFilters({
    tier: '', category: '', verifiedOnly: false, city: '', search: '',
    creatorType: 'all', platform: '', contentType: '', audienceLocation: '',
    creatorStatus: '', minFollowers: '', maxFollowers: '',
  });

  return (
    <div className="min-h-screen bg-[#0B0F14] text-white" data-testid="influencer-discovery-v2">
      {/* ── Hero / Top Bar ─────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 border-b border-white/5 bg-[#0B0F14]/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/client/dashboard')}
            className="text-white/70 hover:text-white hover:bg-white/5"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>

          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-full grid place-items-center"
              style={{ background: `${BRAND}1A`, border: `1px solid ${BRAND}55` }}
            >
              <Sparkles className="w-4 h-4" style={{ color: BRAND }} />
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-bold tracking-tight" data-testid="creator-network-title">Creator Network</h1>
              <p className="text-[11px] text-white/40 leading-tight">Influencers + Content Creators</p>
            </div>
          </div>

          <div className="flex-1" />

          <div className="hidden md:flex items-center gap-2 max-w-xs flex-1">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="Search creators or cities"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#0DEAE3]/40"
                data-testid="search-input"
              />
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(s => !s)}
            className={`relative border-white/10 bg-white/5 text-white hover:bg-white/10 ${showFilters ? 'ring-1 ring-[#0DEAE3]/50' : ''}`}
            data-testid="filters-toggle"
          >
            <Filter className="w-4 h-4 mr-1.5" /> Filters
            {activeFilterCount > 0 && (
              <span
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[10px] grid place-items-center font-bold"
                style={{ background: BRAND, color: '#0B0F14' }}
              >
                {activeFilterCount}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setReloadCounter(c => c + 1)}
            className="text-white/60 hover:text-white hover:bg-white/5"
            data-testid="refresh-btn"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          {/* Sprint 233 — Browse Packages CTA */}
          <Button
            size="sm"
            onClick={() => navigate('/client/packages')}
            className="bg-[#0DEAE3] text-[#0B0F14] hover:bg-[#0DEAE3]/90 font-semibold hidden sm:inline-flex"
            data-testid="browse-packages-cta"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Browse Packages
          </Button>
        </div>

        {/* Mobile search */}
        <div className="md:hidden px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              placeholder="Search creators or cities"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              data-testid="search-input-mobile"
            />
          </div>
        </div>
      </header>

      {/* ── Filter Panel ───────────────────────────────────────────────── */}
      {showFilters && (
        <div className="border-b border-white/5 bg-white/[0.02]" data-testid="filter-panel">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 space-y-5">
            {/* Iter-247 — Creator Type */}
            <div>
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2 flex items-center gap-2">
                <Users className="w-3.5 h-3.5" /> Creator Type
              </div>
              <div className="flex flex-wrap gap-2">
                {(meta.creator_types && meta.creator_types.length > 0 ? meta.creator_types : [
                  { id: 'all', label: 'All Creators' },
                  { id: 'influencer', label: 'Influencers' },
                  { id: 'content_creator', label: 'Content Creators' },
                  { id: 'both', label: 'Both' },
                ]).map(ct => (
                  <TierChip
                    key={ct.id}
                    active={filters.creatorType === ct.id}
                    onClick={() => setFilters({ ...filters, creatorType: ct.id })}
                    data-testid={`creator-type-${ct.id}`}
                  >
                    {ct.label}
                  </TierChip>
                ))}
              </div>
            </div>

            {/* Iter-247 — Platform */}
            <div>
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Platform</div>
              <div className="flex flex-wrap gap-2">
                <TierChip active={!filters.platform} onClick={() => setFilters({ ...filters, platform: '' })}>Any Platform</TierChip>
                {(meta.platforms || []).map(p => (
                  <TierChip
                    key={p.id}
                    active={filters.platform === p.id}
                    onClick={() => setFilters({ ...filters, platform: p.id })}
                    data-testid={`platform-${p.id}`}
                  >
                    {p.label}
                  </TierChip>
                ))}
              </div>
            </div>

            {/* Tiers */}
            <div>
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2 flex items-center gap-2">
                <Award className="w-3.5 h-3.5" /> Influencer Tier
              </div>
              <div className="flex flex-wrap gap-2">
                <TierChip active={!filters.tier} onClick={() => setFilters({ ...filters, tier: '' })}>All Tiers</TierChip>
                {meta.tiers.map(t => (
                  <TierChip
                    key={t.id}
                    active={filters.tier === t.id}
                    onClick={() => setFilters({ ...filters, tier: t.id })}
                    data-testid={`tier-${t.id}`}
                  >
                    {t.label}
                  </TierChip>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div>
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Category</div>
              <div className="flex flex-wrap gap-2">
                <TierChip active={!filters.category} onClick={() => setFilters({ ...filters, category: '' })}>All Categories</TierChip>
                {meta.categories.map(c => (
                  <TierChip
                    key={c}
                    active={filters.category === c}
                    onClick={() => setFilters({ ...filters, category: c })}
                    data-testid={`category-${c.replace(/\s/g, '-').toLowerCase()}`}
                  >
                    {c}
                  </TierChip>
                ))}
              </div>
            </div>

            {/* Iter-247 — Content Type */}
            <div>
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Content Type</div>
              <div className="flex flex-wrap gap-2">
                <TierChip active={!filters.contentType} onClick={() => setFilters({ ...filters, contentType: '' })}>Any Type</TierChip>
                {(meta.content_types || []).map(ct => (
                  <TierChip
                    key={ct}
                    active={filters.contentType === ct}
                    onClick={() => setFilters({ ...filters, contentType: ct })}
                    data-testid={`content-type-${ct.replace(/\s|\//g, '-').toLowerCase()}`}
                  >
                    {ct}
                  </TierChip>
                ))}
              </div>
            </div>

            {/* Iter-247 — Audience Location + Followers Range + City + Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Audience Location</div>
                <Input
                  placeholder="e.g. Atlanta, GA"
                  value={filters.audienceLocation}
                  onChange={(e) => setFilters({ ...filters, audienceLocation: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="audience-location-filter"
                />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Creator City</div>
                <Input
                  placeholder="Any city"
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="city-filter"
                />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Creator Status</div>
                <select
                  value={filters.creatorStatus}
                  onChange={(e) => setFilters({ ...filters, creatorStatus: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2"
                  data-testid="creator-status-filter"
                >
                  <option value="" className="bg-[#0B0F14]">Any status</option>
                  {(meta.creator_statuses || [
                    { id: 'verified', label: 'Verified Creator' },
                    { id: 'pending', label: 'Pending Verification' },
                    { id: 'not_submitted', label: 'Not Submitted' },
                  ]).map(s => (
                    <option key={s.id} value={s.id} className="bg-[#0B0F14]">{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Min Followers</div>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.minFollowers}
                  onChange={(e) => setFilters({ ...filters, minFollowers: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="min-followers-filter"
                />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Max Followers</div>
                <Input
                  type="number"
                  placeholder="No max"
                  value={filters.maxFollowers}
                  onChange={(e) => setFilters({ ...filters, maxFollowers: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="max-followers-filter"
                />
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={filters.verifiedOnly}
                    onChange={(e) => setFilters({ ...filters, verifiedOnly: e.target.checked })}
                    className="accent-[#0DEAE3]"
                    data-testid="verified-only"
                  />
                  Verified only
                </label>
                <div className="flex-1" />
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-white/60 hover:text-white"
                    data-testid="clear-filters"
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Results ───────────────────────────────────────────────────── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-white/50" data-testid="results-count">
            <span className="text-white font-semibold">{influencers.length}</span>{' '}
            creator{influencers.length !== 1 ? 's' : ''} matching your search
          </p>
          <div className="hidden sm:flex items-center gap-1 text-[11px] text-white/30">
            <Eye className="w-3.5 h-3.5" /> Click any card for full preview
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
          </div>
        ) : influencers.length === 0 ? (
          <EmptyState onReset={clearFilters} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="influencer-grid">
            {influencers.map(inf => {
              const picked = packageSlug && pickedForPackage.some(p => (p.user_id || p.id) === inf.user_id);
              return (
                <div key={inf.user_id} className={packageSlug ? 'relative' : ''}>
                  <InfluencerCard
                    influencer={inf}
                    onOpen={() => packageSlug ? togglePackagePick(inf) : setPreviewId(inf.user_id)}
                    onToggleFav={() => toggleFav(inf.user_id)}
                    isFav={favIds.has(inf.user_id)}
                    onInvite={() => handleInvite(inf)}
                    onCompare={() => toggleCompare(inf)}
                    inCompare={compareIds.has(inf.user_id)}
                  />
                  {packageSlug && (
                    <div className={`absolute inset-0 rounded-2xl pointer-events-none ${picked ? 'ring-4 ring-[#0DEAE3] bg-[#0DEAE3]/5' : ''}`}>
                      {picked && (
                        <div className="absolute top-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-[#0DEAE3] text-[#0B0F14] text-[11px] font-bold uppercase tracking-wider">
                          Selected
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      <CreatorPreviewDrawer
        influencer={previewInfluencer}
        open={Boolean(previewInfluencer)}
        onClose={() => setPreviewId(null)}
        onToggleFav={() => previewInfluencer && toggleFav(previewInfluencer.user_id)}
        isFav={previewInfluencer ? favIds.has(previewInfluencer.user_id) : false}
        onViewProfile={() => previewInfluencer && navigate(`/client/creators/${previewInfluencer.user_id}`)}
        onBook={() => previewInfluencer && navigate(`/client/discover/${previewInfluencer.user_id}?action=book`)}
      />

      {/* Sprint 233 — Sticky footer when selecting creators for a package */}
      {packageSlug && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-[#0B0F14]/95 backdrop-blur-md border-t border-[#0DEAE3]/30 px-4 py-3 flex items-center gap-3 shadow-[0_-10px_40px_rgba(13,234,227,0.15)]" data-testid="package-select-footer">
          <Sparkles className="w-4 h-4 text-[#0DEAE3]" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">
              {pickedForPackage.length === 0 ? 'Pick creator(s) for your package' :
               `${pickedForPackage.length} creator${pickedForPackage.length !== 1 ? 's' : ''} selected`}
            </p>
            <p className="text-[11px] text-white/45 truncate">
              Click cards to add/remove. When you&apos;re done, continue to checkout.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { sessionStorage.removeItem(`pkg-select-${packageSlug}`); setPickedForPackage([]); }}
                  className="border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="clear-picks-btn">
            Clear
          </Button>
          <Button onClick={finishPackageSelection}
                  className="bg-[#0DEAE3] text-[#0B0F14] hover:bg-[#0DEAE3]/90 font-semibold" data-testid="continue-to-checkout-btn">
            Continue · {pickedForPackage.length}
          </Button>
        </div>
      )}
    </div>
  );
};

const TierChip = ({ active, children, onClick, ...rest }) => (
  <button
    onClick={onClick}
    {...rest}
    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
      active
        ? 'bg-[#0DEAE3] text-[#0B0F14] border-[#0DEAE3] shadow-[0_0_18px_rgba(13,234,227,0.35)]'
        : 'bg-white/5 text-white/70 border-white/10 hover:border-[#0DEAE3]/50 hover:text-white'
    }`}
  >
    {children}
  </button>
);

const CardSkeleton = () => (
  <div className="rounded-2xl bg-white/[0.03] border border-white/5 overflow-hidden animate-pulse">
    <div className="aspect-[4/5] bg-white/[0.04]" />
    <div className="p-4 space-y-2">
      <div className="h-4 w-2/3 bg-white/[0.06] rounded" />
      <div className="h-3 w-1/3 bg-white/[0.06] rounded" />
    </div>
  </div>
);

const EmptyState = ({ onReset }) => (
  <div className="text-center py-20" data-testid="empty-state">
    <div className="inline-flex w-16 h-16 rounded-full bg-white/5 border border-white/10 items-center justify-center mb-4">
      <Users className="w-7 h-7 text-white/40" />
    </div>
    <h3 className="text-lg font-semibold text-white">No creators match your filters yet</h3>
    <p className="text-sm text-white/50 mt-1">Try broadening your tier, category, or city filters.</p>
    <Button onClick={onReset} className="mt-5 bg-[#0DEAE3] text-[#0B0F14] hover:bg-[#0DEAE3]/90">
      Reset filters
    </Button>
  </div>
);

export default InfluencerDiscoveryV2;
